
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AgentMessage, AgentType, ThreatPriority, InteractionMode } from '../types';

interface AgentOrchestrationContextType {
  // Message passing
  sendMessage: (message: Omit<AgentMessage, 'id' | 'timestamp'>) => void;
  subscribe: (agent: AgentType, callback: (msg: AgentMessage) => void) => () => void;
  
  // State management
  mode: InteractionMode;
  setMode: (mode: InteractionMode) => void;
  isMainChatActive: boolean;
  setIsMainChatActive: (active: boolean) => void;
  
  // Vision observer control
  visionMode: 'auto' | 'on-demand' | 'paused';
  setVisionMode: (mode: 'auto' | 'on-demand' | 'paused') => void;
  requestVisionSnapshot: () => Promise<string | null>;
  
  // Threat reporting
  onThreatDetected: (priority: ThreatPriority, message: string) => void;
  
  // Mode transitions
  transitionToVoice: () => void;
  transitionToText: () => void;
  transitionToVision: () => void;
  transitionToMultimodal: () => void;
}

const AgentOrchestrationContext = createContext<AgentOrchestrationContextType | null>(null);

interface AgentOrchestrationProviderProps {
  children: React.ReactNode;
}

export const AgentOrchestrationProvider: React.FC<AgentOrchestrationProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<InteractionMode>('text');
  const [isMainChatActive, setIsMainChatActive] = useState(false);
  const [visionMode, setVisionMode] = useState<'auto' | 'on-demand' | 'paused'>('auto');
  
  // Message system
  const subscribers = useRef<Record<AgentType, ((msg: AgentMessage) => void)[]>>({ 
    master: [], 
    vision: [], 
    watchdog: [] 
  });
  const messageIdCounter = useRef(0);

  // Message sending
  const sendMessage = useCallback((message: Omit<AgentMessage, 'id' | 'timestamp'>) => {
    const fullMessage: AgentMessage = {
      ...message,
      id: `msg-${++messageIdCounter.current}`,
      timestamp: Date.now()
    };
    
    console.log(`[Orchestration] ${fullMessage.from} -> ${fullMessage.to}:`, fullMessage.payload);
    
    // Deliver to subscribers
    const targetSubscribers = subscribers.current[fullMessage.to] || [];
    targetSubscribers.forEach(callback => {
      try {
        callback(fullMessage);
      } catch (e) {
        console.error(`[Orchestration] Error delivering message to ${fullMessage.to}:`, e);
      }
    });
  }, []);

  // Subscription management
  const subscribe = useCallback((agent: AgentType, callback: (msg: AgentMessage) => void) => {
    if (!subscribers.current[agent]) {
      subscribers.current[agent] = [];
    }
    subscribers.current[agent].push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = subscribers.current[agent].indexOf(callback);
      if (index > -1) {
        subscribers.current[agent].splice(index, 1);
      }
    };
  }, []);

  // Mode transitions
  const transitionToVoice = useCallback(() => {
    setMode('voice');
    setIsMainChatActive(true);
    // Pause observer auto-descriptions but keep monitoring
    setVisionMode('auto'); // Keep monitoring but suppress non-critical alerts
  }, []);

  const transitionToText = useCallback(() => {
    setMode('text');
    setIsMainChatActive(false);
    // Resume normal observer behavior
    setVisionMode('auto');
  }, []);

  const transitionToVision = useCallback(() => {
    setMode('vision');
    // Special handling for active monitoring
    setVisionMode('auto');
  }, []);

  const transitionToMultimodal = useCallback(() => {
    setMode('multimodal');
    setIsMainChatActive(true);
    setVisionMode('auto');
  }, []);

  // Vision snapshot request
  const requestVisionSnapshot = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      // Send request to vision agent
      sendMessage({
        from: 'master',
        to: 'vision',
        type: 'query',
        payload: { action: 'capture_snapshot' }
      });
      
      // Set up one-time listener for response
      const unsubscribe = subscribe('vision', (msg) => {
        if (msg.from === 'vision' && msg.payload?.type === 'snapshot_result') {
          unsubscribe();
          resolve(msg.payload.data || null);
        }
      });
      
      // Timeout fallback
      setTimeout(() => {
        unsubscribe();
        resolve(null);
      }, 5000);
    });
  }, [sendMessage, subscribe]);

  // Threat detection handler
  const onThreatDetected = useCallback((priority: ThreatPriority, message: string) => {
    // Send threat report to master agent
    sendMessage({
      from: 'vision',
      to: 'master',
      type: 'report',
      payload: {
        type: 'threat_detected',
        priority,
        message,
        timestamp: Date.now()
      }
    });
    
    // Also send to watchdog for system awareness
    sendMessage({
      from: 'vision',
      to: 'watchdog',
      type: 'report',
      payload: {
        type: 'security_alert',
        priority,
        message
      }
    });
  }, [sendMessage]);

  // Prevent message loops by tracking recent message IDs
  const recentMessages = useRef<Set<string>>(new Set());
  const MAX_RECENT_MESSAGES = 100;
  
  useEffect(() => {
    // Clean up old message IDs periodically
    const interval = setInterval(() => {
      if (recentMessages.current.size > MAX_RECENT_MESSAGES) {
        const iterator = recentMessages.current.values();
        for (let i = 0; i < 20; i++) {
          const value = iterator.next();
          if (value.done) break;
          recentMessages.current.delete(value.value);
        }
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const contextValue: AgentOrchestrationContextType = {
    // Message system
    sendMessage,
    subscribe,
    
    // State
    mode,
    setMode,
    isMainChatActive,
    setIsMainChatActive,
    
    // Vision control
    visionMode,
    setVisionMode,
    requestVisionSnapshot,
    
    // Threats
    onThreatDetected,
    
    // Transitions
    transitionToVoice,
    transitionToText,
    transitionToVision,
    transitionToMultimodal
  };

  return (
    <AgentOrchestrationContext.Provider value={contextValue}>
      {children}
    </AgentOrchestrationContext.Provider>
  );
};

// Hook for consuming the context
export const useAgentOrchestration = () => {
  const context = useContext(AgentOrchestrationContext);
  if (!context) {
    throw new Error('useAgentOrchestration must be used within AgentOrchestrationProvider');
  }
  return context;
};

export default AgentOrchestrationContext;
